import axios, { AxiosInstance, AxiosError } from "axios";
import {
  ApiResponse,
  PagedResult,
  LoginRequest,
  RegisterRequest,
  UserInfo,
  LoginResponse,
  CreateUserDto,
  UpdateUserDto,
  DepartmentDto,
  CreateDepartmentDto,
  UpdateDepartmentDto,
  DepartmentFilterParams,
  StateDto,
  CreateStateDto,
  UpdateStateDto,
  StateFilterParams,
  DistrictDto,
  CreateDistrictDto,
  UpdateDistrictDto,
  DistrictFilterParams,
  BlockDto,
  CreateBlockDto,
  UpdateBlockDto,
  BlockFilterParams,
  IndicatorDto,
  CreateIndicatorDto,
  UpdateIndicatorDto,
  IndicatorFrequencyDto,
  IndicatorPeriodDto,
  IndicatorValueDto,
  GroupedIndicatorDataDto,
  AllGroupedIndicatorDataDto,
  CreateIndicatorValueDto,
  UpdateIndicatorValueDto,
  IndicatorValueAuditDto,
  PaginationParams,
  IndicatorFilterParams,
  DashboardStatsDto,
} from "@/lib/types/api";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true, // Important for cookie-based authentication
    });

    // Request interceptor (for future token-based auth if needed)
    this.client.interceptors.request.use(
      (config) => {
        // Add any request preprocessing here
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        // Handle 401 Unauthorized - redirect to login
        if (error.response?.status === 401) {
          // Only redirect to login if we're on a protected route
          if (typeof window !== "undefined") {
            const pathname = window.location.pathname;
            const publicRoutes = ["/", "/login", "/register", "/forgot-password"];
            const isPublicRoute = publicRoutes.includes(pathname);
            
            // Don't redirect if already on login page or on a public route
            if (!isPublicRoute && !pathname.includes("/login")) {
              window.location.href = "/login";
            }
          }
        }

        // Enhance error with user-friendly message from response
        if (error.response?.data) {
          const data = error.response.data as any;
          
          // Extract error message from API response
          if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
            error.message = data.errors.join(', ');
          } else if (data.message) {
            error.message = data.message;
          } else if (data.error) {
            error.message = data.error;
          }
        } else if (!error.response) {
          // Network error
          error.message = 'Network error. Please check your connection and try again.';
        }
        
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(data: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    const response = await this.client.post<ApiResponse<LoginResponse>>(
      "/api/auth/login",
      data
    );
    return response.data;
  }

  async register(data: RegisterRequest): Promise<ApiResponse<LoginResponse>> {
    const response = await this.client.post<ApiResponse<LoginResponse>>(
      "/api/auth/register",
      data
    );
    return response.data;
  }

  async logout(): Promise<ApiResponse<boolean>> {
    const response = await this.client.post<ApiResponse<boolean>>("/api/auth/logout");
    return response.data;
  }

  async getCurrentUser(): Promise<ApiResponse<UserInfo>> {
    const response = await this.client.get<ApiResponse<UserInfo>>("/api/auth/me");
    return response.data;
  }

  async checkAuth(): Promise<ApiResponse<boolean>> {
    const response = await this.client.get<ApiResponse<boolean>>("/api/auth/check");
    return response.data;
  }

  // User management endpoints (Admin only)
  async getUsers(params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    role?: string;
    districtId?: string;
    sortBy?: string;
    sortDescending?: boolean;
  }): Promise<ApiResponse<PagedResult<UserInfo>>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.role) queryParams.append('role', params.role);
    if (params?.districtId) queryParams.append('districtId', params.districtId);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortDescending !== undefined) queryParams.append('sortDescending', params.sortDescending.toString());
    
    const url = `/api/users${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await this.client.get<ApiResponse<PagedResult<UserInfo>>>(url);
    return response.data;
  }

  async getUserById(id: string): Promise<ApiResponse<UserInfo>> {
    const response = await this.client.get<ApiResponse<UserInfo>>(`/api/users/${id}`);
    return response.data;
  }

  async createUser(data: CreateUserDto): Promise<ApiResponse<UserInfo>> {
    const response = await this.client.post<ApiResponse<UserInfo>>("/api/users", data);
    return response.data;
  }

  async updateUser(id: string, data: UpdateUserDto): Promise<ApiResponse<UserInfo>> {
    const response = await this.client.put<ApiResponse<UserInfo>>(`/api/users/${id}`, data);
    return response.data;
  }

  async deleteUser(id: string): Promise<ApiResponse<boolean>> {
    const response = await this.client.delete<ApiResponse<boolean>>(`/api/users/${id}`);
    return response.data;
  }

  // Profile endpoints
  async getProfile(): Promise<ApiResponse<UserInfo>> {
    const response = await this.client.get<ApiResponse<UserInfo>>("/api/profile");
    return response.data;
  }

  async updateProfile(data: { firstName?: string; lastName?: string; email?: string; selectedTheme?: string }): Promise<ApiResponse<UserInfo>> {
    const response = await this.client.put<ApiResponse<UserInfo>>("/api/profile", data);
    return response.data;
  }

  async changePassword(data: { currentPassword: string; newPassword: string; confirmPassword: string }): Promise<ApiResponse<boolean>> {
    const response = await this.client.post<ApiResponse<boolean>>("/api/profile/change-password", data);
    return response.data;
  }

  // Password reset endpoints
  async forgotPassword(data: { usernameOrEmail: string }): Promise<ApiResponse<boolean>> {
    const response = await this.client.post<ApiResponse<boolean>>("/api/auth/forgot-password", data);
    return response.data;
  }

  async resetPassword(data: { usernameOrEmail: string; otp: string; newPassword: string; confirmPassword: string }): Promise<ApiResponse<boolean>> {
    const response = await this.client.post<ApiResponse<boolean>>("/api/auth/reset-password", data);
    return response.data;
  }

  // Department endpoints
  async getDepartments(): Promise<ApiResponse<DepartmentDto[]>> {
    const response = await this.client.get<ApiResponse<DepartmentDto[]>>(
      "/api/v1/departments"
    );
    return response.data;
  }

  async getDepartmentsPaged(
    params: PaginationParams
  ): Promise<ApiResponse<PagedResult<DepartmentDto>>> {
    const response = await this.client.get<ApiResponse<PagedResult<DepartmentDto>>>(
      "/api/v1/departments/paged",
      { params }
    );
    return response.data;
  }

  async getDepartmentsPagedWithFilters(
    params: DepartmentFilterParams
  ): Promise<ApiResponse<PagedResult<DepartmentDto>>> {
    const response = await this.client.get<ApiResponse<PagedResult<DepartmentDto>>>(
      "/api/v1/departments/paged-with-filters",
      { params }
    );
    return response.data;
  }

  async getActiveDepartments(): Promise<ApiResponse<DepartmentDto[]>> {
    const response = await this.client.get<ApiResponse<DepartmentDto[]>>(
      "/api/v1/departments/active"
    );
    return response.data;
  }

  async getDepartmentById(id: string): Promise<ApiResponse<DepartmentDto>> {
    const response = await this.client.get<ApiResponse<DepartmentDto>>(
      `/api/v1/departments/${id}`
    );
    return response.data;
  }

  // Indicator Frequencies
  async getIndicatorFrequencies(): Promise<ApiResponse<IndicatorFrequencyDto[]>> {
    const response = await this.client.get<ApiResponse<IndicatorFrequencyDto[]>>(
      "/api/v1/indicatorfrequencies"
    );
    return response.data;
  }

  async createDepartment(
    data: CreateDepartmentDto
  ): Promise<ApiResponse<DepartmentDto>> {
    const response = await this.client.post<ApiResponse<DepartmentDto>>(
      "/api/v1/departments",
      data
    );
    return response.data;
  }

  async updateDepartment(
    id: string,
    data: UpdateDepartmentDto
  ): Promise<ApiResponse<DepartmentDto>> {
    const response = await this.client.put<ApiResponse<DepartmentDto>>(
      `/api/v1/departments/${id}`,
      data
    );
    return response.data;
  }

  async deleteDepartment(id: string): Promise<ApiResponse<boolean>> {
    const response = await this.client.delete<ApiResponse<boolean>>(
      `/api/v1/departments/${id}`
    );
    return response.data;
  }

  // State endpoints
  async getStates(): Promise<ApiResponse<StateDto[]>> {
    const response = await this.client.get<ApiResponse<StateDto[]>>("/api/v1/states");
    return response.data;
  }

  async getStatesPaged(
    params: PaginationParams
  ): Promise<ApiResponse<PagedResult<StateDto>>> {
    const response = await this.client.get<ApiResponse<PagedResult<StateDto>>>(
      "/api/v1/states/paged",
      { params }
    );
    return response.data;
  }

  async getStatesPagedWithFilters(
    params: StateFilterParams
  ): Promise<ApiResponse<PagedResult<StateDto>>> {
    const response = await this.client.get<ApiResponse<PagedResult<StateDto>>>(
      "/api/v1/states/paged-with-filters",
      { params }
    );
    return response.data;
  }

  async getActiveStates(): Promise<ApiResponse<StateDto[]>> {
    const response = await this.client.get<ApiResponse<StateDto[]>>(
      "/api/v1/states/active"
    );
    return response.data;
  }

  async getStateById(id: string): Promise<ApiResponse<StateDto>> {
    const response = await this.client.get<ApiResponse<StateDto>>(
      `/api/v1/states/${id}`
    );
    return response.data;
  }

  async createState(data: CreateStateDto): Promise<ApiResponse<StateDto>> {
    const response = await this.client.post<ApiResponse<StateDto>>(
      "/api/v1/states",
      data
    );
    return response.data;
  }

  async updateState(
    id: string,
    data: UpdateStateDto
  ): Promise<ApiResponse<StateDto>> {
    const response = await this.client.put<ApiResponse<StateDto>>(
      `/api/v1/states/${id}`,
      data
    );
    return response.data;
  }

  async deleteState(id: string): Promise<ApiResponse<boolean>> {
    const response = await this.client.delete<ApiResponse<boolean>>(
      `/api/v1/states/${id}`
    );
    return response.data;
  }

  // District endpoints
  async getDistricts(): Promise<ApiResponse<DistrictDto[]>> {
    const response = await this.client.get<ApiResponse<DistrictDto[]>>(
      "/api/v1/districts"
    );
    return response.data;
  }

  async getDistrictsPaged(
    params: PaginationParams
  ): Promise<ApiResponse<PagedResult<DistrictDto>>> {
    const response = await this.client.get<ApiResponse<PagedResult<DistrictDto>>>(
      "/api/v1/districts/paged",
      { params }
    );
    return response.data;
  }

  async getDistrictsPagedWithFilters(
    params: DistrictFilterParams
  ): Promise<ApiResponse<PagedResult<DistrictDto>>> {
    const response = await this.client.get<ApiResponse<PagedResult<DistrictDto>>>(
      "/api/v1/districts/paged-with-filters",
      { params }
    );
    return response.data;
  }

  async getDistrictsByState(stateId: string): Promise<ApiResponse<DistrictDto[]>> {
    const response = await this.client.get<ApiResponse<DistrictDto[]>>(
      `/api/v1/districts/state/${stateId}`
    );
    return response.data;
  }

  async getDistrictById(id: string): Promise<ApiResponse<DistrictDto>> {
    const response = await this.client.get<ApiResponse<DistrictDto>>(
      `/api/v1/districts/${id}`
    );
    return response.data;
  }

  async createDistrict(
    data: CreateDistrictDto
  ): Promise<ApiResponse<DistrictDto>> {
    const response = await this.client.post<ApiResponse<DistrictDto>>(
      "/api/v1/districts",
      data
    );
    return response.data;
  }

  async updateDistrict(
    id: string,
    data: UpdateDistrictDto
  ): Promise<ApiResponse<DistrictDto>> {
    const response = await this.client.put<ApiResponse<DistrictDto>>(
      `/api/v1/districts/${id}`,
      data
    );
    return response.data;
  }

  async deleteDistrict(id: string): Promise<ApiResponse<boolean>> {
    const response = await this.client.delete<ApiResponse<boolean>>(
      `/api/v1/districts/${id}`
    );
    return response.data;
  }

  // Block endpoints
  async getBlocks(): Promise<ApiResponse<BlockDto[]>> {
    const response = await this.client.get<ApiResponse<BlockDto[]>>(
      "/api/v1/blocks"
    );
    return response.data;
  }

  async getBlocksPaged(
    params: PaginationParams
  ): Promise<ApiResponse<PagedResult<BlockDto>>> {
    const response = await this.client.get<ApiResponse<PagedResult<BlockDto>>>(
      "/api/v1/blocks/paged",
      { params }
    );
    return response.data;
  }

  async getBlocksPagedWithFilters(
    params: BlockFilterParams
  ): Promise<ApiResponse<PagedResult<BlockDto>>> {
    const response = await this.client.get<ApiResponse<PagedResult<BlockDto>>>(
      "/api/v1/blocks/paged-with-filters",
      { params }
    );
    return response.data;
  }

  async getBlocksByDistrict(districtId: string): Promise<ApiResponse<BlockDto[]>> {
    const response = await this.client.get<ApiResponse<BlockDto[]>>(
      `/api/v1/blocks/district/${districtId}`
    );
    return response.data;
  }

  async getBlockById(id: string): Promise<ApiResponse<BlockDto>> {
    const response = await this.client.get<ApiResponse<BlockDto>>(
      `/api/v1/blocks/${id}`
    );
    return response.data;
  }

  async createBlock(data: CreateBlockDto): Promise<ApiResponse<BlockDto>> {
    const response = await this.client.post<ApiResponse<BlockDto>>(
      "/api/v1/blocks",
      data
    );
    return response.data;
  }

  async updateBlock(
    id: string,
    data: UpdateBlockDto
  ): Promise<ApiResponse<BlockDto>> {
    const response = await this.client.put<ApiResponse<BlockDto>>(
      `/api/v1/blocks/${id}`,
      data
    );
    return response.data;
  }

  async deleteBlock(id: string): Promise<ApiResponse<boolean>> {
    const response = await this.client.delete<ApiResponse<boolean>>(
      `/api/v1/blocks/${id}`
    );
    return response.data;
  }

  // Indicator endpoints
  async getIndicators(): Promise<ApiResponse<IndicatorDto[]>> {
    const response = await this.client.get<ApiResponse<IndicatorDto[]>>(
      "/api/v1/indicators"
    );
    return response.data;
  }

  async getIndicatorsPaged(
    params: PaginationParams
  ): Promise<ApiResponse<PagedResult<IndicatorDto>>> {
    const response = await this.client.get<ApiResponse<PagedResult<IndicatorDto>>>(
      "/api/v1/indicators/paged",
      { params }
    );
    return response.data;
  }

  async getIndicatorsPagedWithFilters(
    params: IndicatorFilterParams
  ): Promise<ApiResponse<PagedResult<IndicatorDto>>> {
    // Clean up params to remove undefined values
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== undefined && value !== null && value !== '')
    );
    
    const response = await this.client.get<ApiResponse<PagedResult<IndicatorDto>>>(
      "/api/v1/indicators/paged-with-filters",
      { params: cleanParams }
    );
    return response.data;
  }

  async getActiveIndicators(): Promise<ApiResponse<IndicatorDto[]>> {
    const response = await this.client.get<ApiResponse<IndicatorDto[]>>(
      "/api/v1/indicators/active"
    );
    return response.data;
  }

  async getBlockLevelIndicators(): Promise<ApiResponse<IndicatorDto[]>> {
    const response = await this.client.get<ApiResponse<IndicatorDto[]>>(
      "/api/v1/indicators/block-level"
    );
    return response.data;
  }

  async getDistrictLevelIndicators(): Promise<ApiResponse<IndicatorDto[]>> {
    const response = await this.client.get<ApiResponse<IndicatorDto[]>>(
      "/api/v1/indicators/district-level"
    );
    return response.data;
  }

  async getIndicatorsByDepartment(
    departmentId: string
  ): Promise<ApiResponse<IndicatorDto[]>> {
    const response = await this.client.get<ApiResponse<IndicatorDto[]>>(
      `/api/v1/indicators/department/${departmentId}`
    );
    return response.data;
  }

  async getIndicatorById(id: string): Promise<ApiResponse<IndicatorDto>> {
    const response = await this.client.get<ApiResponse<IndicatorDto>>(
      `/api/v1/indicators/${id}`
    );
    return response.data;
  }

  async createIndicator(
    data: CreateIndicatorDto
  ): Promise<ApiResponse<IndicatorDto>> {
    const response = await this.client.post<ApiResponse<IndicatorDto>>(
      "/api/v1/indicators",
      data
    );
    return response.data;
  }

  async updateIndicator(
    id: string,
    data: UpdateIndicatorDto
  ): Promise<ApiResponse<IndicatorDto>> {
    const response = await this.client.put<ApiResponse<IndicatorDto>>(
      `/api/v1/indicators/${id}`,
      data
    );
    return response.data;
  }

  async deleteIndicator(id: string): Promise<ApiResponse<boolean>> {
    const response = await this.client.delete<ApiResponse<boolean>>(
      `/api/v1/indicators/${id}`
    );
    return response.data;
  }

  // Indicator Values
  async createIndicatorValue(
    data: CreateIndicatorValueDto
  ): Promise<ApiResponse<IndicatorValueDto>> {
    const response = await this.client.post<ApiResponse<IndicatorValueDto>>(
      "/api/v1/indicatorvalues",
      data
    );
    return response.data;
  }

  async getIndicatorValueById(id: string): Promise<ApiResponse<IndicatorValueDto>> {
    const response = await this.client.get<ApiResponse<IndicatorValueDto>>(
      `/api/v1/indicatorvalues/${id}`
    );
    return response.data;
  }

  async getIndicatorValuesByBlockId(
    blockId: string,
    page: number = 1,
    pageSize: number = 10,
    search?: string,
    frequency?: string
  ): Promise<ApiResponse<PagedResult<IndicatorValueDto>>> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    if (search) params.append('search', search);
    if (frequency && frequency !== 'all') params.append('frequency', frequency);
    
    const response = await this.client.get<ApiResponse<PagedResult<IndicatorValueDto>>>(
      `/api/v1/indicatorvalues/block/${blockId}?${params.toString()}`
    );
    return response.data;
  }

  async getIndicatorValuesByDistrictId(districtId: string): Promise<ApiResponse<IndicatorValueDto[]>> {
    const response = await this.client.get<ApiResponse<IndicatorValueDto[]>>(
      `/api/v1/indicatorvalues/district/${districtId}`
    );
    return response.data;
  }

  async getGroupedIndicatorDataByBlockId(
    blockId: string,
    page: number = 1,
    pageSize: number = 10,
    search?: string,
    frequency?: string
  ): Promise<ApiResponse<PagedResult<GroupedIndicatorDataDto>>> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    if (search) params.append('search', search);
    if (frequency && frequency !== 'all') params.append('frequency', frequency);
    
    const response = await this.client.get<ApiResponse<PagedResult<GroupedIndicatorDataDto>>>(
      `/api/v1/indicatorvalues/block/${blockId}/grouped?${params.toString()}`
    );
    return response.data;
  }

  async getGroupedIndicatorDataByDistrictId(
    districtId: string,
    page: number = 1,
    pageSize: number = 10,
    search?: string,
    frequency?: string
  ): Promise<ApiResponse<PagedResult<GroupedIndicatorDataDto>>> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    if (search) params.append('search', search);
    if (frequency && frequency !== 'all') params.append('frequency', frequency);
    
    const response = await this.client.get<ApiResponse<PagedResult<GroupedIndicatorDataDto>>>(
      `/api/v1/indicatorvalues/district/${districtId}/grouped?${params.toString()}`
    );
    return response.data;
  }

  async updateIndicatorValue(
    id: string,
    data: UpdateIndicatorValueDto
  ): Promise<ApiResponse<IndicatorValueDto>> {
    const response = await this.client.put<ApiResponse<IndicatorValueDto>>(
      `/api/v1/indicatorvalues/${id}`,
      data
    );
    return response.data;
  }

  async getIndicatorValueAuditHistory(indicatorValueId: string): Promise<ApiResponse<IndicatorValueAuditDto[]>> {
    const response = await this.client.get<ApiResponse<IndicatorValueAuditDto[]>>(
      `/api/v1/indicatorvalues/${indicatorValueId}/audit-history`
    );
    return response.data;
  }

  async getAvailablePeriods(frequencyId: string): Promise<ApiResponse<IndicatorPeriodDto[]>> {
    const response = await this.client.get<ApiResponse<IndicatorPeriodDto[]>>(
      `/api/v1/indicatorvalues/periods/${frequencyId}`
    );
    return response.data;
  }

  async getAllGroupedIndicatorData(
    page: number = 1,
    pageSize: number = 10,
    search?: string,
    frequency?: string,
    stateId?: string,
    districtId?: string,
    blockId?: string,
    departmentId?: string,
    programType?: string
  ): Promise<ApiResponse<PagedResult<AllGroupedIndicatorDataDto>>> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    if (search) params.append('search', search);
    if (frequency) params.append('frequency', frequency);
    if (stateId) params.append('stateId', stateId);
    if (districtId) params.append('districtId', districtId);
    if (blockId) params.append('blockId', blockId);
    if (departmentId) params.append('departmentId', departmentId);
    if (programType) params.append('programType', programType);

    const response = await this.client.get<ApiResponse<PagedResult<AllGroupedIndicatorDataDto>>>(
      `/api/v1/indicatorvalues/all/grouped?${params}`
    );
    return response.data;
  }

  // Dashboard endpoints
  async getDashboardStats(): Promise<ApiResponse<DashboardStatsDto>> {
    const response = await this.client.get<ApiResponse<DashboardStatsDto>>(
      "/api/v1/dashboard/stats"
    );
    return response.data;
  }
}

export const apiClient = new ApiClient();
